import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  Row,
  Col,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Button,
  Alert,
} from 'reactstrap';

import { showError, showInfo } from '../../actions/notificationActions';
import { getUrlParameter } from '../../utils';
import {
  getConsentUrl,
  registerEmail,
  getAccounts,
  toggleAccountStatus,
  updateWhitelistedSenders,
} from '../../services/gmail.service';

import Table from './Table';
import WhitelistModal from './WhitelistModal';

class GmailAccounts extends Component {
  state = {
    accounts: [],
    loading: false,
    limit: 20,
    offset: 0,
    order: 'desc',
    meta: { count: 0 },

    // Toggling state — tracks which email is being toggled
    togglingEmail: null,

    // OAuth flow
    connecting: false,
    callbackProcessing: false,

    // Whitelist modal
    whitelistModalOpen: false,
    whitelistEmail: null,
    whitelistSenders: [],
    savingWhitelist: false,
  };

  componentDidMount() {
    this.loadAccounts();
    this.handleOAuthCallback();
  }

  /**
   * If the page loaded with a ?code= query param it means Google just
   * redirected back after consent. Forward the code to the backend.
   */
  handleOAuthCallback = () => {
    const code = getUrlParameter('code', window.location.search);
    if (!code) return;

    this.setState({ callbackProcessing: true });

    registerEmail(code)
      .then((res) => {
        this.setState({ callbackProcessing: false });
        this.props.dispatch(
          showInfo(res.message || 'Email registered successfully!')
        );
        // Clean the URL so the code isn't reused on refresh
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        this.loadAccounts();
      })
      .catch((err) => {
        this.setState({ callbackProcessing: false });
        this.props.dispatch(showError(err));
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      });
  };

  loadAccounts = () => {
    this.setState({ loading: true });
    const { limit, offset, order } = this.state;

    getAccounts({ limit, offset, order })
      .then((res) => {
        this.setState({
          loading: false,
          accounts: res.data || [],
          meta: res.meta || { count: 0 },
        });
      })
      .catch((err) => {
        this.setState({ loading: false });
        this.props.dispatch(showError(err));
      });
  };

  handleConnectGmail = () => {
    this.setState({ connecting: true });

    getConsentUrl()
      .then((res) => {
        // Redirect the admin to Google's consent screen
        window.location.href = res.data;
      })
      .catch((err) => {
        this.setState({ connecting: false });
        this.props.dispatch(showError(err));
      });
  };

  handleToggleStatus = (emailAddress, isActive) => {
    this.setState({ togglingEmail: emailAddress });

    toggleAccountStatus(emailAddress, isActive)
      .then(() => {
        this.setState({ togglingEmail: null });
        this.props.dispatch(
          showInfo(
            `Sync ${isActive ? 'resumed' : 'paused'} for ${emailAddress}`
          )
        );
        this.loadAccounts();
      })
      .catch((err) => {
        this.setState({ togglingEmail: null });
        this.props.dispatch(showError(err));
      });
  };

  handleOpenWhitelistModal = (emailAddress, whitelistedSenders) => {
    this.setState({
      whitelistModalOpen: true,
      whitelistEmail: emailAddress,
      whitelistSenders: whitelistedSenders,
    });
  };

  handleCloseWhitelistModal = () => {
    this.setState({
      whitelistModalOpen: false,
      whitelistEmail: null,
      whitelistSenders: [],
    });
  };

  handleSaveWhitelist = (emailAddress, whitelistedSenders) => {
    this.setState({ savingWhitelist: true });

    updateWhitelistedSenders(emailAddress, whitelistedSenders)
      .then(() => {
        this.setState({ savingWhitelist: false, whitelistModalOpen: false });
        this.props.dispatch(
          showInfo(`Whitelisted senders updated for ${emailAddress}`)
        );
        this.loadAccounts();
      })
      .catch((err) => {
        this.setState({ savingWhitelist: false });
        this.props.dispatch(showError(err));
      });
  };

  handleNextPage = () => {
    const { offset, limit, meta } = this.state;
    const nextOffset = offset + limit;
    if (nextOffset >= meta.count) return;

    this.setState({ offset: nextOffset }, this.loadAccounts);
  };

  handlePrevPage = () => {
    const { offset, limit } = this.state;
    if (offset === 0) return;

    this.setState({ offset: Math.max(0, offset - limit) }, this.loadAccounts);
  };

  render() {
    const {
      accounts,
      loading,
      togglingEmail,
      connecting,
      callbackProcessing,
      offset,
      limit,
      meta,
      whitelistModalOpen,
      whitelistEmail,
      whitelistSenders,
      savingWhitelist,
    } = this.state;

    const currentPage = Math.floor(offset / limit) + 1;
    const totalPages = Math.ceil((meta.count || 0) / limit) || 1;

    return (
      <div className="content">
        <Row>
          <Col xs={12}>
            <Card>
              <CardHeader className="ml-2 d-flex justify-content-between align-items-center">
                <div>
                  <CardTitle>Gmail Accounts</CardTitle>
                  <p className="category">
                    Manage registered Gmail accounts for email sync.
                  </p>
                </div>
                <div>
                  <Button
                    color="primary"
                    onClick={this.handleConnectGmail}
                    disabled={connecting || callbackProcessing}
                  >
                    <i className="now-ui-icons ui-1_simple-add mr-1" />
                    {connecting ? 'Redirecting...' : 'Connect Gmail'}
                  </Button>
                </div>
              </CardHeader>

              <CardBody style={{ padding: 0 }}>
                {callbackProcessing && (
                  <Alert color="info" className="text-center m-3">
                    <strong>Processing OAuth callback...</strong> Please wait
                    while we register your email account.
                  </Alert>
                )}

                <Table
                  accounts={accounts}
                  loading={loading}
                  onToggle={this.handleToggleStatus}
                  togglingEmail={togglingEmail}
                  onEditWhitelist={this.handleOpenWhitelistModal}
                  footer={
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <span className="text-muted small">
                          Page {currentPage} of {totalPages}
                          {meta.count > 0 && ` • ${meta.count} total`}
                        </span>
                      </div>
                      <div>
                        <Button
                          color="primary"
                          size="sm"
                          onClick={this.handlePrevPage}
                          disabled={offset === 0}
                          className="mr-2"
                        >
                          &larr; Prev
                        </Button>
                        <Button
                          color="primary"
                          size="sm"
                          onClick={this.handleNextPage}
                          disabled={offset + limit >= meta.count}
                        >
                          Next &rarr;
                        </Button>
                      </div>
                    </div>
                  }
                />
              </CardBody>

              {/* Whitelist Modal */}
              <WhitelistModal
                isOpen={whitelistModalOpen}
                onClose={this.handleCloseWhitelistModal}
                onSave={this.handleSaveWhitelist}
                emailAddress={whitelistEmail}
                whitelistedSenders={whitelistSenders}
                saving={savingWhitelist}
              />
            </Card>
          </Col>
        </Row>
      </div>
    );
  }
}

export default connect()(GmailAccounts);
